import { createIdempotencyKey } from "@lafayette/api-client";
import type {
  StaffMemoryAcceptBody,
  StaffMemoryCommandOutcome,
  StaffMemoryCorrectBody,
  StaffMemoryRecordBody,
  StaffMemoryVersionReasonBody,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { describeMemoryError, type MemoryFailure } from "./errors";
import { memoryKeys } from "./queries";
import { useMemoryRuntime } from "./runtime";

// Runs a staff memory command (M-S4 to M-S12) and keeps the pages current. Three rules live here:
//   1. Idempotency. One user action has one `Idempotency-Key`. After a failure where the command may
//      have run (no answer, a 5xx, a 429) the next identical attempt reuses the key, so the server runs
//      it once and replays the stored answer. A changed input, or an action after a success or a refusal,
//      gets a new key (the same key with another body would be refused as a key reused).
//   2. Version. Every command that changes a fact sends the `expectedVersion` the page read. The page
//      never guesses a version: a stale one is a 409 and the user decides again.
//   3. Reload. After a success, or after a refusal that says the memory moved (409), every memory read is
//      marked stale and the open pages read again.
//
// Nothing here logs. A body holds a value or a reason code, and neither goes anywhere but the request.

export type VersionedCommandName =
  | "activate"
  | "reject"
  | "confirm"
  | "dispute"
  | "resolve"
  | "delete";

export type MemoryCommand =
  | Readonly<{ command: "record"; customerId: string; body: StaffMemoryRecordBody }>
  | Readonly<{ command: VersionedCommandName; factId: string; body: StaffMemoryVersionReasonBody }>
  | Readonly<{ command: "accept"; factId: string; body: StaffMemoryAcceptBody }>
  | Readonly<{ command: "correct"; factId: string; body: StaffMemoryCorrectBody }>;

export type MemoryCommandResult =
  | Readonly<{ ok: true; outcome: StaffMemoryCommandOutcome }>
  | Readonly<{ ok: false; failure: MemoryFailure }>;

const signatureOf = (request: MemoryCommand): string =>
  JSON.stringify([
    request.command,
    "customerId" in request ? request.customerId : request.factId,
    request.body,
  ]);

export function useMemoryCommands() {
  const { api } = useMemoryRuntime();
  const queryClient = useQueryClient();
  const keys = useRef(new Map<string, string>());
  // A failure that may have run (no answer, 429, 5xx) leaves the page unsure whether the change happened. The page
  // reads again when the staff member closes the dialog, so it never keeps showing the state from before.
  const mayHaveRun = useRef(false);
  const [pendingCount, setPendingCount] = useState(0);

  const send = useCallback(
    (request: MemoryCommand, idempotencyKey: string): Promise<StaffMemoryCommandOutcome> => {
      const options = { idempotencyKey };
      switch (request.command) {
        case "record":
          return api.recordFact(request.customerId, request.body, options);
        case "activate":
          return api.activateFact(request.factId, request.body, options);
        case "reject":
          return api.rejectCandidate(request.factId, request.body, options);
        case "confirm":
          return api.confirmFact(request.factId, request.body, options);
        case "dispute":
          return api.disputeFact(request.factId, request.body, options);
        case "resolve":
          return api.resolveDispute(request.factId, request.body, options);
        case "delete":
          return api.deleteFact(request.factId, request.body, options);
        case "accept":
          return api.acceptCandidate(request.factId, request.body, options);
        case "correct":
          return api.correctFact(request.factId, request.body, options);
      }
    },
    [api],
  );

  const run = useCallback(
    async (request: MemoryCommand): Promise<MemoryCommandResult> => {
      const signature = signatureOf(request);
      const key = keys.current.get(signature) ?? createIdempotencyKey();
      keys.current.set(signature, key);

      setPendingCount((count) => count + 1);
      try {
        const outcome = await send(request, key);
        keys.current.delete(signature);
        mayHaveRun.current = false;
        void queryClient.invalidateQueries({ queryKey: memoryKeys.all });
        return { ok: true, outcome };
      } catch (error) {
        const failure = describeMemoryError(error);
        if (!failure.retryable) keys.current.delete(signature);
        else mayHaveRun.current = true;
        if (failure.reload) void queryClient.invalidateQueries({ queryKey: memoryKeys.all });
        return { ok: false, failure };
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [queryClient, send],
  );

  /** Call when a dialog closes: after a failure that may have run, the memory is read again. */
  const reloadAfterClose = useCallback(() => {
    if (!mayHaveRun.current) return;
    mayHaveRun.current = false;
    void queryClient.invalidateQueries({ queryKey: memoryKeys.all });
  }, [queryClient]);

  return { run, pending: pendingCount > 0, reloadAfterClose };
}
